import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import axios from "axios";
import {serializeAxiosError} from "../../../../Config/Reducers/ReducerUtils";
import {Tracker} from "../../../../Shared/Types/Tracker";
import {ChartNcu} from "../../../../Shared/Types/ChartNcu";
import {format, set} from "date-fns";
import {LocalDateTimeFormatISO} from "../../../../Utils/DateFormatPatternUtils";

export interface TrackerState {
    loading: boolean;
    errorMessage: string | null;
    entities: NcuEquipment[];
    entity: any;
    updating: false;
    updateSuccess: false;
    idsNcu: number[];
    periodTrackers: any | null;
    activePowerPlantId: number | null;
    activeLoadRequestId: string | null;
    activeDetailContextId: string | null;
    activeDetailRequestId: string | null;
    activeDetailNcuId: number | null;
    activeDetailPeriodKey: string | null;
}

export type TrackerRequestKind = "initial" | "refresh";

export interface GetTrackerEntitiesArgs {
    powerPlantId: number;
    ncuId: number;
    periodTrackers: any;
    loadRequestId: string;
    contextId: string;
    requestKind: TrackerRequestKind;
}

type DateInput = Date | string | number | null | undefined;

interface NormalizedNcuDayPeriod {
    groupBy: "day";
    fromDateTime: Date;
    toDateTime: Date;
}

const toValidDate = (value?: DateInput): Date | undefined => {
    if (value == null) {
        return undefined;
    }

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

const formatPeriodDateTime = (value?: DateInput): string | undefined => {
    const date = toValidDate(value);
    return date ? LocalDateTimeFormatISO(date) : undefined;
};

const getDefaultTrackerPeriod = () => {
    const toDateTime = new Date();
    const fromDateTime = new Date(toDateTime);
    fromDateTime.setDate(toDateTime.getDate() - 1);

    return {
        groupBy: "day",
        fromDateTime,
        toDateTime
    };
};

export const normalizeNcuDayPeriod = (
    period: { fromDateTime?: DateInput; toDateTime?: DateInput; groupBy?: string }
): NormalizedNcuDayPeriod => {
    const day = toValidDate(period.toDateTime) ?? toValidDate(period.fromDateTime) ?? new Date();
    return {
        groupBy: "day",
        fromDateTime: set(day, { hours: 4, minutes: 0, seconds: 0, milliseconds: 0 }),
        toDateTime: set(day, { hours: 21, minutes: 0, seconds: 0, milliseconds: 0 }),
    };
};

export const isSameTrackerPeriod = (
    left?: { fromDateTime?: DateInput; toDateTime?: DateInput } | null,
    right?: { fromDateTime?: DateInput; toDateTime?: DateInput } | null,
): boolean => {
    const leftFrom = formatPeriodDateTime(left?.fromDateTime);
    const leftTo = formatPeriodDateTime(left?.toDateTime);
    const rightFrom = formatPeriodDateTime(right?.fromDateTime);
    const rightTo = formatPeriodDateTime(right?.toDateTime);

    if (!leftFrom || !leftTo || !rightFrom || !rightTo) {
        return false;
    }

    return leftFrom === rightFrom && leftTo === rightTo;
};

const isCurrentNcuListRequest = (state: TrackerState, powerPlantId: number, requestId: string) => (
    state.activePowerPlantId === powerPlantId && state.activeLoadRequestId === requestId
);

const trackerPeriodKey = (period: { fromDateTime?: DateInput; toDateTime?: DateInput }) => (
    `${formatPeriodDateTime(period.fromDateTime)}|${formatPeriodDateTime(period.toDateTime)}`
);

const isCurrentTrackerDetailRequest = (state: TrackerState, args: GetTrackerEntitiesArgs, requestId: string) => {
    if (!isCurrentNcuListRequest(state, args.powerPlantId, args.loadRequestId)) {
        return false;
    }

    const requestedPeriod = normalizeNcuDayPeriod(args.periodTrackers ?? getDefaultTrackerPeriod());
    const currentPeriod = normalizeNcuDayPeriod(state.periodTrackers ?? getDefaultTrackerPeriod());
    return isSameTrackerPeriod(requestedPeriod, currentPeriod) &&
        state.activeDetailContextId === args.contextId &&
        state.activeDetailRequestId === requestId &&
        state.activeDetailNcuId === args.ncuId &&
        state.activeDetailPeriodKey === trackerPeriodKey(requestedPeriod);
};

const cloneNcuEquipment = (source: any) => ({
    ...source,
    trackers: Array.isArray(source.trackers) ? source.trackers.map((tracker: any) => ({...tracker})) : source.trackers,
    chart: source.chart ? {
        ...source.chart,
        xAxis: source.chart.xAxis?.map((axis: any) => ({
            ...axis,
            axisLabel: axis.axisLabel ? {...axis.axisLabel} : axis.axisLabel
        })),
        yAxis: source.chart.yAxis?.map((axis: any) => ({
            ...axis,
            axisLabel: axis.axisLabel ? {...axis.axisLabel} : axis.axisLabel
        })),
        series: source.chart.series?.map((series: any) => ({
            ...series,
            data: series.data?.map((point: any[]) => [...point]),
            dataByTime: series.dataByTime?.map((point: any[]) => [...point]),
            markArea: Array.isArray(series.markArea)
                ? series.markArea.map((area: any) => Array.isArray(area) ? [...area] : {...area})
                : series.markArea
        }))
    } : source.chart
});

export const prepareNcuEquipment = (source: any) => {
    const data = cloneNcuEquipment(source);
    const chart = data.chart;
    const chartSeries = chart?.series?.length > 0 ? chart.series : [];
    const xAxis = chart?.xAxis?.[0];

    if (xAxis !== undefined) {
        const windowSize = window.innerWidth;
        xAxis.minInterval = windowSize < 771 ? 10800000 : 3600000;
        xAxis.maxInterval = windowSize < 771 ? 10800000 : 3600000;
        xAxis.axisLabel = {
            formatter: (value: any) => format(new Date(value), 'HH:mm'),
        };
    }

    const yAxis = chart?.yAxis?.[0];
    if (yAxis !== undefined) {
        yAxis.axisLabel = {formatter: '{value} °'};
        yAxis.min = -180;
        yAxis.max = 180;
    }

    if (chartSeries.length > 0) {
        const markAreaData: any[][] = [];
        const alarms = chartSeries[0].markArea;
        if (Array.isArray(alarms)) {
            for (let index = 0; index < alarms.length; index += 2) {
                markAreaData.push([alarms[index], alarms[index + 1]]);
            }
        }

        chartSeries.forEach((series: any) => {
            series.data = (series.dataByTime ?? []).map(([time, value]: [string, unknown]) => {
                if (value === 'null' || value === null || value === undefined || value === '') {
                    return [time, null];
                }
                const numericValue = Number(value);
                return [time, Number.isFinite(numericValue) ? numericValue : null];
            });
            series.showSymbol = false;
            series.connectNulls = false;
            series.markArea = {
                itemStyle: {color: 'rgba(255, 173, 177, 0.4)'},
                data: markAreaData
            };
        });
    }

    return data;
};

export class NcuEquipment {
    id: number;
    name: string;
    windSpeed: number;
    windDirection: number;
    trackers: Tracker[];
    chart: ChartNcu | null;
    ncuName: string;

    constructor(id: number, name: string, windSpeed: number, windDirection: number, trackers: Tracker[], chart: ChartNcu | null, ncuName: string) {
        this.id = id;
        this.name = name;
        this.windSpeed = windSpeed;
        this.windDirection = windDirection;
        this.trackers = trackers;
        this.chart = chart;
        this.ncuName = ncuName;
    }
}

const initialState: TrackerState = {
    loading: false,
    errorMessage: null,
    entities: new Array<NcuEquipment>(),
    entity: undefined,
    updating: false,
    updateSuccess: false,
    idsNcu: [],
    periodTrackers: getDefaultTrackerPeriod(),
    activePowerPlantId: null,
    activeLoadRequestId: null,
    activeDetailContextId: null,
    activeDetailRequestId: null,
    activeDetailNcuId: null,
    activeDetailPeriodKey: null
}

const apiUrl = 'api/power-station'

export const getEntities = createAsyncThunk<any, GetTrackerEntitiesArgs>(
    'power Plant/fetch_entity_list',
    async ({powerPlantId, ncuId, periodTrackers}, {signal}) => {
        const period = normalizeNcuDayPeriod(periodTrackers ?? getDefaultTrackerPeriod());
        return axios.get<NcuEquipment>(`${apiUrl}/trackers/${powerPlantId}?ncuId=${ncuId}&startDateTime=${LocalDateTimeFormatISO(period.fromDateTime)}&endDateTime=${LocalDateTimeFormatISO(period.toDateTime)}`, {
            timeout: 30000,
            signal
        });
    },
    { serializeError: serializeAxiosError }
);

export const getNcus = createAsyncThunk<any, number>(
    'power plant/fetch_ncu_list',
    async (id) => {
        return axios.get<number[]>(`${apiUrl}/listNcu/${id}`);
    }
)

const TrackerReducer = createSlice({
    name: 'TrackerReducer',
    initialState,
    reducers: {
        reset() {
            return initialState;
        },
        clearEntities: (state) => {
            state.entity = undefined
        },
        clearIds: (state) => {
            state.idsNcu = []
        },
        setPeriodTrackers: (state, action) => {
            const normalized = normalizeNcuDayPeriod(action.payload);
            if (isSameTrackerPeriod(state.periodTrackers, normalized)) {
                return;
            }
            state.periodTrackers = normalized;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getNcus.pending, (state, action) => {
                state.activePowerPlantId = action.meta.arg;
                state.activeLoadRequestId = action.meta.requestId;
                state.idsNcu = [];
                state.entity = undefined;
                state.errorMessage = null;
                state.loading = true;
                state.activeDetailContextId = null;
                state.activeDetailRequestId = null;
                state.activeDetailNcuId = null;
                state.activeDetailPeriodKey = null;
            })
            .addCase(getNcus.fulfilled, (state, action) => {
                if (!isCurrentNcuListRequest(state, action.meta.arg, action.meta.requestId)) {
                    return;
                }

                state.loading = false;
                state.idsNcu = action.payload?.data?.data ?? [];
            })
            .addCase(getNcus.rejected, (state, action) => {
                if (!isCurrentNcuListRequest(state, action.meta.arg, action.meta.requestId)) {
                    return;
                }

                state.entity = undefined;
                state.loading = false;
                state.errorMessage = "Dados de Trackers não encontrados.";
            })
            .addCase(getEntities.pending, (state, action) => {
                const requestedPeriod = normalizeNcuDayPeriod(action.meta.arg.periodTrackers ?? getDefaultTrackerPeriod());
                const currentPeriod = normalizeNcuDayPeriod(state.periodTrackers ?? getDefaultTrackerPeriod());
                if (!isCurrentNcuListRequest(state, action.meta.arg.powerPlantId, action.meta.arg.loadRequestId) ||
                    !isSameTrackerPeriod(requestedPeriod, currentPeriod)) {
                    return;
                }

                state.activeDetailContextId = action.meta.arg.contextId;
                state.activeDetailRequestId = action.meta.requestId;
                state.activeDetailNcuId = action.meta.arg.ncuId;
                state.activeDetailPeriodKey = trackerPeriodKey(requestedPeriod);
                state.errorMessage = null;
                state.loading = true;
            })
            .addCase(getEntities.fulfilled, (state, action) => {
                if (!isCurrentTrackerDetailRequest(state, action.meta.arg, action.meta.requestId)) {
                    return;
                }

                const responseData = action.payload?.data?.data;
                if (!responseData || responseData.id !== action.meta.arg.ncuId) {
                    state.loading = false;
                    state.entity = undefined;
                    state.errorMessage = "Dados de Trackers não encontrados.";
                    return;
                }

                const data = prepareNcuEquipment(responseData);

                state.loading = false;
                state.errorMessage = null;
                state.entity = data;
            })
            .addCase(getEntities.rejected, (state, action) => {
                if (!isCurrentTrackerDetailRequest(state, action.meta.arg, action.meta.requestId)) {
                    return;
                }

                state.loading = false;
                state.errorMessage = "Dados de Trackers não encontrados.";
            });
    },
});
export const {clearEntities, clearIds, setPeriodTrackers} = TrackerReducer.actions;

export default TrackerReducer.reducer;
